/**
 * Live-session HTTP client.
 *
 * - list open sessions: GET /users/{userId}/live-sessions (self only)
 * - join response includes playerId (used for · YOU highlight)
 * - current-roles may 404 with no active round → treat as empty
 * - live-state currentRound / strategy / myRole may be null between rounds
 * - write ops: strategist only
 */
import { apiFetch } from '../../lib/apiClient';
import type {
  CreateMatchRequest,
  CurrentRolesResponse,
  EndMatchResponse,
  LiveSessionPlayerResponse,
  LiveSessionResponse,
  LiveStateResponse,
  MatchResponse,
  MatchRoundResponse,
  OpenLiveSessionsResponse,
  RoundHistoryResponse,
  StrategySummary,
} from './types';

function teamSessionsBase(teamId: string) {
  return `/teams/${encodeURIComponent(teamId)}/live-sessions`;
}

function sessionBase(teamId: string, sessionId: string) {
  return `${teamSessionsBase(teamId)}/${encodeURIComponent(sessionId)}`;
}

function matchBase(teamId: string, sessionId: string, matchId: string) {
  return `${sessionBase(teamId, sessionId)}/matches/${encodeURIComponent(matchId)}`;
}

function roundBase(teamId: string, sessionId: string, matchId: string, roundId: string) {
  return `${matchBase(teamId, sessionId, matchId)}/rounds/${encodeURIComponent(roundId)}`;
}

/**
 * Lists open live sessions for teams the user belongs to (with nested live matches).
 * userId must match the authenticated user.
 */
export function listOpenLiveSessions(userId: string): Promise<OpenLiveSessionsResponse> {
  return apiFetch<OpenLiveSessionsResponse>(
    `/users/${encodeURIComponent(userId)}/live-sessions`,
  );
}

/** Strategist: opens a new live session (no match yet). */
export function createLiveSession(teamId: string): Promise<LiveSessionResponse> {
  return apiFetch<LiveSessionResponse>(teamSessionsBase(teamId), {
    method: 'POST',
  });
}

/** Strategist: creates a live match inside an open session. */
export function createMatch(
  teamId: string,
  sessionId: string,
  body: CreateMatchRequest,
): Promise<MatchResponse> {
  return apiFetch<MatchResponse>(`${sessionBase(teamId, sessionId)}/matches`, {
    method: 'POST',
    body,
  });
}

export function joinLiveSession(teamId: string, sessionId: string): Promise<LiveSessionPlayerResponse> {
  return apiFetch<LiveSessionPlayerResponse>(`${sessionBase(teamId, sessionId)}/join`, {
    method: 'POST',
  });
}

export function getLiveState(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<LiveStateResponse> {
  return apiFetch<LiveStateResponse>(`${matchBase(teamId, sessionId, matchId)}/live-state`);
}

export function getCurrentRoles(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<CurrentRolesResponse> {
  return apiFetch<CurrentRolesResponse>(`${matchBase(teamId, sessionId, matchId)}/current-roles`);
}

export function getRoundHistory(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<RoundHistoryResponse> {
  return apiFetch<RoundHistoryResponse>(`${matchBase(teamId, sessionId, matchId)}/round-history`);
}

export function listMapStrategies(
  teamId: string,
  map: string,
  side?: string | null,
): Promise<StrategySummary[]> {
  const sideNorm = (side ?? '').trim().toUpperCase();
  const query =
    sideNorm === 'T' || sideNorm === 'CT'
      ? `?side=${encodeURIComponent(sideNorm)}`
      : '';
  return apiFetch<StrategySummary[]>(
    `/teams/${encodeURIComponent(teamId)}/maps/${encodeURIComponent(map)}/strategies${query}`,
  );
}

export function createRound(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${matchBase(teamId, sessionId, matchId)}/rounds`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

/** Prefer this over createRound for the product “Next round” control. */
export function nextRound(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${matchBase(teamId, sessionId, matchId)}/next-round`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

export function endMatch(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<EndMatchResponse> {
  return apiFetch<EndMatchResponse>(`${matchBase(teamId, sessionId, matchId)}/end`, {
    method: 'POST',
  });
}

/** Sets our team's starting side (T/CT) for the match. */
export function setStartingSide(
  teamId: string,
  sessionId: string,
  matchId: string,
  startingSide: 'T' | 'CT',
): Promise<unknown> {
  return apiFetch(`${matchBase(teamId, sessionId, matchId)}/starting-side`, {
    method: 'POST',
    body: { startingSide },
  });
}

/** Sets strategy on the current preparable (pending/buy) round — no roundId required. */
export function setMatchStrategy(
  teamId: string,
  sessionId: string,
  matchId: string,
  strategyId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${matchBase(teamId, sessionId, matchId)}/strategy`, {
    method: 'POST',
    body: { strategyId },
  }).then(normalizeMatchRound);
}

export function setRoundStrategy(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
  strategyId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/strategy`, {
    method: 'POST',
    body: { strategyId },
  }).then(normalizeMatchRound);
}

export function startBuyPhase(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/buy-phase`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

export function startRound(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/start`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

export function endRound(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
  result: 'win' | 'loss',
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/end`, {
    method: 'POST',
    body: { result },
  }).then(normalizeMatchRound);
}

export function setRoundClock(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
  clockSecs: number,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/clock`, {
    method: 'POST',
    body: { clockSecs },
  }).then(normalizeMatchRound);
}

export function confirmBombPlant(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/bomb-plant`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

export function confirmBombDefuse(
  teamId: string,
  sessionId: string,
  matchId: string,
  roundId: string,
): Promise<MatchRoundResponse> {
  return apiFetch<MatchRoundResponse>(`${roundBase(teamId, sessionId, matchId, roundId)}/bomb-defuse`, {
    method: 'POST',
  }).then(normalizeMatchRound);
}

/** Normalize domain MatchRound which may use sql.Null* object shapes. */
function normalizeMatchRound(raw: unknown): MatchRoundResponse {
  if (!raw || typeof raw !== 'object') {
    return { id: '' };
  }
  const r = raw as Record<string, unknown>;
  return {
    id: String(r.id ?? r.Id ?? ''),
    matchId: optionalString(r.matchId ?? r.MatchId),
    roundNumber: optionalNumber(r.roundNumber ?? r.RoundNumber),
    strategyId: optionalString(unwrapNullish(r.strategyId ?? r.StrategyId)),
    status: optionalString(r.status ?? r.Status),
    result: optionalString(unwrapNullish(r.result ?? r.Result)),
    phaseClockSecs: optionalNumber(unwrapNullish(r.phaseClockSecs ?? r.PhaseClockSecs)),
    phaseStartedAt: optionalTime(unwrapNullish(r.phaseStartedAt ?? r.PhaseStartedAt)),
    bombPlantedAt: optionalTime(unwrapNullish(r.bombPlantedAt ?? r.BombPlantedAt)),
    bombDefusedAt: optionalTime(unwrapNullish(r.bombDefusedAt ?? r.BombDefusedAt)),
  };
}

function unwrapNullish(value: unknown): unknown {
  if (value && typeof value === 'object' && 'Valid' in (value as object)) {
    const v = value as { Valid: boolean; String?: string; Time?: string; Int64?: number };
    if (!v.Valid) return null;
    if (v.String !== undefined) return v.String;
    if (v.Time !== undefined) return v.Time;
    if (v.Int64 !== undefined) return v.Int64;
  }
  return value;
}

function optionalString(value: unknown): string | undefined {
  if (value == null) return undefined;
  return String(value);
}

function optionalNumber(value: unknown): number | undefined {
  if (value == null) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function optionalTime(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  return undefined;
}
